<template>
  <div>
    <!-- Admin Stats Section -->
    <div class="admin-stats-container">
      <div class="admin-stat" style="width: 100%; display: flex; gap: 40px">
        <div class="admin-table" style="margin-bottom: 0px; width: fit-content">
          <div class="admin-table td" style="margin-bottom: 0px">
            <div class="admin-list-title color-primary">Total Views</div>
            <div style="text-align: center">
              <p class="admin-stat-mins">{{ totalViews }}</p>
            </div>
          </div>
        </div>
        <div class="admin-table" style="margin-bottom: 0px; width: fit-content">
          <div class="admin-table td" style="margin-bottom: 0px">
            <div class="admin-list-title color-primary">
              Views by Anonymous Users
            </div>
            <div style="text-align: center">
              <p class="admin-stat-mins">{{ totalAnonymousViews }}</p>
            </div>
          </div>
        </div>
        <div class="admin-table" style="margin-bottom: 0px; width: fit-content">
          <div class="admin-table td" style="margin-bottom: 0px">
            <div class="admin-list-title color-primary">
              Views by Logged In Users
            </div>
            <div style="text-align: center">
              <p class="admin-stat-mins">{{ totalLoggedInViews }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter Controls -->
    <div
      class="makercart-filters"
      v-if="!loadingMakercartAnalytics && !errorMakercartAnalytics"
    >
      <div class="filter-row">
        <div>
          <div style="display: flex; gap: 10px; align-items: end">
            <div class="filter-group">
              <label class="filter-label">Start Date:</label>
              <input
                type="date"
                class="filter-date-input"
                :class="{
                  'partial-filter': isPartialDateFilter,
                  'ready-filter': isDateFilterReady,
                }"
                v-model="filters.start_date"
                @change="handleFilterChange"
              />
            </div>
            <div class="filter-group">
              <label class="filter-label">End Date:</label>
              <input
                type="date"
                class="filter-date-input"
                :class="{
                  'partial-filter': isPartialDateFilter,
                  'ready-filter': isDateFilterReady,
                }"
                v-model="filters.end_date"
                @change="handleFilterChange"
              />
            </div>
            <button
              v-if="isDateFilterReady"
              @click="clearDateFilter"
              class="filter-clear-btn"
              title="Clear date filter"
            >
              Clear
            </button>
          </div>
          <div v-if="isPartialDateFilter" class="filter-status">
            <small class="filter-hint">
              Please fill both start and end dates to apply filter
            </small>
          </div>
          <div v-if="isDateFilterReady" class="filter-status">
            <small class="filter-applied"> ✓ Date filter applied </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loadingMakercartAnalytics" class="loading-state">Loading...</div>

    <!-- Error State -->
    <div v-if="errorMakercartAnalytics" class="makercart-error-state">
      {{ errorMakercartAnalytics }}
    </div>

    <!-- Result State -->
    <div
      v-if="!loadingMakercartAnalytics && !errorMakercartAnalytics"
      class="makercart-table-container"
    >
      <table class="makercart-table">
        <thead>
          <tr>
            <th colspan="3" style="border-top-left-radius: 10px"></th>
            <th
              colspan="5"
              style="text-align: center; border-top-right-radius: 10px"
            >
              Lesson Materials
            </th>
          </tr>
          <tr>
            <th class="dashboard-sortable-header">Rank</th>
            <th
              class="dashboard-sortable-header"
              @click="sortColumn('title')"
              :style="{ cursor: 'pointer' }"
            >
              <div class="header-with-tooltip">
                Activity Title
                <img
                  src="/images/filter-table.svg"
                  style="margin-left: 5px; max-height: 20px"
                  alt="filter"
                />
              </div>
            </th>
            <th
              class="dashboard-sortable-header"
              @click="sortColumn('makercart_clicks')"
              :style="{ cursor: 'pointer' }"
            >
              <div class="header-with-tooltip">
                <div>
                  <span>Makercart Clicks</span>
                  <v-tooltip
                    location="top"
                    :open-on-hover="true"
                    content-class="custom-tooltip-table"
                    :open-delay="100"
                    :close-delay="100"
                    transition="fade-transition"
                    :eager="false"
                    max-width="300"
                    text="Number of times users view an Makercart activity in MC Studio
                  (aggregated across all users)"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon size="14" class="info-icon ml-1" v-bind="props"
                        >mdi-information</v-icon
                      >
                    </template>
                  </v-tooltip>
                </div>
                <img
                  src="/images/filter-table.svg"
                  style="margin-left: 5px; max-height: 20px"
                  alt="filter"
                />
              </div>
            </th>
            <th
              class="dashboard-sortable-header"
              @click="sortColumn('total_clicks')"
              :style="{ cursor: 'pointer' }"
            >
              <div class="header-with-tooltip">
                <div>
                  <span>Total Clicks</span>
                  <v-tooltip
                    location="top"
                    :open-on-hover="true"
                    content-class="custom-tooltip-table"
                    :open-delay="100"
                    :close-delay="100"
                    transition="fade-transition"
                    :eager="false"
                    max-width="300"
                    text="Number of times each instructional material is attempted to be
                    accessed for each activity [broken down into 4 cases by the
                    following columns]"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon size="14" class="info-icon ml-1" v-bind="props"
                        >mdi-information</v-icon
                      >
                    </template>
                  </v-tooltip>
                </div>
                <img
                  src="/images/filter-table.svg"
                  style="margin-left: 5px; max-height: 20px"
                  alt="filter"
                />
              </div>
            </th>
            <th
              class="dashboard-sortable-header"
              @click="sortColumn('anonymous_clicks')"
              :style="{ cursor: 'pointer' }"
            >
              <div class="header-with-tooltip">
                <div>
                  <span>Anonymous Successful</span>
                  <v-tooltip
                    location="top"
                    :open-on-hover="true"
                    content-class="custom-tooltip-table"
                    :open-delay="100"
                    :close-delay="100"
                    transition="fade-transition"
                    :eager="false"
                    max-width="300"
                    text="(0-3 lessons available for pre-login access) Anonymous user →
                  successful access"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon size="14" class="info-icon ml-1" v-bind="props"
                        >mdi-information</v-icon
                      >
                    </template>
                  </v-tooltip>
                </div>
                <img
                  src="/images/filter-table.svg"
                  style="margin-left: 5px; max-height: 20px"
                  alt="filter"
                />
              </div>
            </th>
            <th
              class="dashboard-sortable-header"
              @click="sortColumn('login_clicks')"
              :style="{ cursor: 'pointer' }"
            >
              <div class="header-with-tooltip">
                <div>
                  <span>Logged In Successful</span>
                  <v-tooltip
                    location="top"
                    :open-on-hover="true"
                    content-class="custom-tooltip-table"
                    :open-delay="100"
                    :close-delay="100"
                    transition="fade-transition"
                    :eager="false"
                    max-width="300"
                    text="(0-3 lessons available for pre-login access) Logged-in user →
                  successful access"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon size="14" class="info-icon ml-1" v-bind="props"
                        >mdi-information</v-icon
                      >
                    </template>
                  </v-tooltip>
                </div>
                <img
                  src="/images/filter-table.svg"
                  style="margin-left: 5px; max-height: 20px"
                  alt="filter"
                />
              </div>
            </th>
            <th
              class="dashboard-sortable-header"
              @click="sortColumn('abandoned_login_clicks')"
              :style="{ cursor: 'pointer' }"
            >
              <div class="header-with-tooltip">
                <div>
                  <span>Abandoned Sign In</span>
                  <v-tooltip
                    location="top"
                    :open-on-hover="true"
                    content-class="custom-tooltip-table"
                    :open-delay="100"
                    :close-delay="100"
                    transition="fade-transition"
                    :eager="false"
                    max-width="300"
                    :style="{
                      color: 'red !important',
                    }"
                    text="(once users have exhausted 3 pre-login access lessons)
                  Prompted to Sign In register → abandoned → not successful
                  access"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon size="14" class="info-icon ml-1" v-bind="props"
                        >mdi-information</v-icon
                      >
                    </template>
                  </v-tooltip>
                </div>
                <img
                  src="/images/filter-table.svg"
                  style="margin-left: 5px; max-height: 20px"
                  alt="filter"
                />
              </div>
            </th>
            <th
              class="dashboard-sortable-header"
              @click="sortColumn('prompted_login_clicks')"
              :style="{ cursor: 'pointer' }"
            >
              <div class="header-with-tooltip">
                <div>
                  <span>Prompted Sign In</span>
                  <v-tooltip
                    location="top"
                    :open-on-hover="true"
                    content-class="custom-tooltip-table"
                    :open-delay="100"
                    :close-delay="100"
                    transition="fade-transition"
                    :eager="false"
                    max-width="300"
                    text="(once users have exhausted 3 pre-login access lessons)
                  Prompted to sign in or register → successfully logged in →
                  successfully access"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon size="14" class="info-icon ml-1" v-bind="props"
                        >mdi-information</v-icon
                      >
                    </template>
                  </v-tooltip>
                </div>
                <img
                  src="/images/filter-table.svg"
                  style="margin-left: 5px; max-height: 20px"
                  alt="filter"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Filter/Sort Loading -->
          <template v-if="loadingFilters">
            <tr style="position: relative">
              <td colspan="8" style="padding: 10px; border: none">
                <div class="spinner-container">
                  <div class="spinner"></div>
                </div>
              </td>
            </tr>
          </template>
          <!-- Actual Data -->
          <template v-else>
            <tr v-if="!makercartAnalytics?.length" style="position: relative">
              <td
                colspan="8"
                style="
                  text-align: center;
                  padding: 20px;
                  font-size: var(--fs-medium);
                "
              >
                No data available
              </td>
            </tr>
            <tr
              v-else
              v-for="(item, index) in makercartAnalytics"
              :key="item.id"
            >
              <td>{{ (currentPage - 1) * pageLimit + index + 1 }}</td>
              <td style="text-align: left">{{ item.title }}</td>
              <td>{{ item.makercart_clicks }}</td>
              <td>{{ item.total_clicks }}</td>
              <td>{{ item.anonymous_clicks }}</td>
              <td>{{ item.login_clicks }}</td>
              <td>{{ item.abandoned_login_clicks }}</td>
              <td>{{ item.prompted_login_clicks }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <template
      v-if="
        makercartAnalytics?.length &&
        !loadingMakercartAnalytics &&
        !loadingFilters
      "
    >
      <BasePagination
        :total-pages="totalPages"
        :model-value="currentPage"
        @update:model-value="updatePage"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, defineExpose, computed, watch } from "vue";
import { debounce } from "lodash";
import MakercartActivityService from "@/services/makercartActivityService.js";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";
import BasePagination from "@/components/common/BasePagination.vue";

// Makercart Studio analytics data
const makercartAnalytics = ref([]);
const loadingMakercartAnalytics = ref(false); // For initial loading
const loadingFilters = ref(false); // For filter/sort operations
const errorMakercartAnalytics = ref(null);

// Pagination state
const currentPage = ref(1);
const totalPages = ref(1);
const totalRows = ref(0);
const pageLimit = ref(10);

// Filters state similar to ListPage.vue
const filters = ref({
  start_date: "",
  end_date: "",
  sort_by: "makercart_clicks",
  sort_dir: "desc",
  page: 1,
  limit: 10,
});

// Setup URL query sync with filters
useUrlQuerySync({
  filters,
  excludeDefaults: [
    { sort_by: "makercart_clicks" },
    { sort_dir: "desc" },
    { page: 1 },
    { limit: 10 },
  ],
});

// Sorting state
const currentSort = ref({ column: "makercart_clicks", sort_dir: "desc" });

// Computed property to check if date filter is ready to be applied
const isDateFilterReady = computed(() => {
  return filters.value.start_date && filters.value.end_date;
});

// Computed property to check if only one date is filled
const isPartialDateFilter = computed(() => {
  return (
    (filters.value.start_date && !filters.value.end_date) ||
    (!filters.value.start_date && filters.value.end_date)
  );
});

// Computed properties for admin stats
const totalViews = computed(() => {
  return makercartAnalytics.value.reduce((total, item) => {
    return total + (item.makercart_clicks || 0);
  }, 0);
});

const totalAnonymousViews = computed(() => {
  return makercartAnalytics.value.reduce((total, item) => {
    return total + (item.anonymous_clicks || 0);
  }, 0);
});

const totalLoggedInViews = computed(() => {
  return makercartAnalytics.value.reduce((total, item) => {
    return total + (item.login_clicks || 0);
  }, 0);
});

// Debounced filter change handler
const handleFilterChange = debounce(() => {
  // Only apply filter if both start_date and end_date are provided, or if clearing
  if (
    (filters.value.start_date && filters.value.end_date) ||
    (!filters.value.start_date && !filters.value.end_date)
  ) {
    // Reset to first page when applying date filters
    currentPage.value = 1;
    filters.value.page = 1;
    getMakercartAnalytics();
  }
}, 300);

// Function to update page and emit event
const updatePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getMakercartAnalytics();
};

// Function to clear date filter
const clearDateFilter = () => {
  filters.value.start_date = "";
  filters.value.end_date = "";
  // Reset to first page when clearing filters
  currentPage.value = 1;
  filters.value.page = 1;
  getMakercartAnalytics();
};

// Sort column function similar to TableData.vue
const sortColumn = (column) => {
  if (currentSort.value.column === column) {
    currentSort.value.sort_dir =
      currentSort.value.sort_dir === "asc" ? "desc" : "asc";
  } else {
    currentSort.value.column = column;
    currentSort.value.sort_dir = "asc";
  }

  // Update filters with new sort parameters
  filters.value.sort_by = column;
  filters.value.sort_dir = currentSort.value.sort_dir;

  // Reset to first page when sorting
  currentPage.value = 1;
  filters.value.page = 1;

  // Fetch new data with updated sort
  getMakercartAnalytics();
};

const getMakercartAnalytics = async (isInitialLoad = false) => {
  if (isInitialLoad) {
    loadingMakercartAnalytics.value = true;
  } else {
    loadingFilters.value = true;
  }
  errorMakercartAnalytics.value = null;

  try {
    // Prepare parameters for API call
    const params = {};

    // Add date filters if they exist
    if (filters.value.start_date) {
      params.start_date = filters.value.start_date;
    }
    if (filters.value.end_date) {
      params.end_date = filters.value.end_date;
    }

    // Add sorting parameters
    if (filters.value.sort_by) {
      params.sort_by = filters.value.sort_by;
    }
    if (filters.value.sort_dir) {
      params.sort_dir = filters.value.sort_dir;
    }

    // Add pagination parameters
    params.page = filters.value.page || 1;
    params.limit = filters.value.limit || 10;

    const response = await MakercartActivityService.getAnalytics(params);
    if (response.status === 200) {
      const responseData = response.data.data;
      makercartAnalytics.value = responseData.rows || [];
      currentPage.value = responseData.page || 1;
      totalPages.value = responseData.total_page || 1;
      totalRows.value = responseData.total_rows || 0;
      pageLimit.value = responseData.limit || 10;
    } else {
      errorMakercartAnalytics.value = response.message;
    }
  } catch (error) {
    errorMakercartAnalytics.value = error.message;
  } finally {
    if (isInitialLoad) {
      loadingMakercartAnalytics.value = false;
    } else {
      loadingFilters.value = false;
    }
  }
};

// Watch for filter changes and sync with currentSort
watch(
  [() => filters.value.sort_by, () => filters.value.sort_dir],
  ([sortBy, sortDir]) => {
    currentSort.value.column = sortBy;
    currentSort.value.sort_dir = sortDir;
  },
  { immediate: true }
);

// Watch for page changes and sync with filters
watch(
  () => filters.value.page,
  (newPage) => {
    if (newPage !== currentPage.value) {
      currentPage.value = newPage;
    }
  },
  { immediate: true }
);

// Watch for date filter changes to auto-apply when both dates are set
watch(
  [() => filters.value.start_date, () => filters.value.end_date],
  ([startDate, endDate]) => {
    // Auto-apply filter when both dates are provided
    if (startDate && endDate) {
      handleFilterChange();
    }
  }
);

// Expose the function to parent component
defineExpose({
  getMakercartAnalytics,
});

// Load data when component mounts
onMounted(() => {
  getMakercartAnalytics(true);
});
</script>

<style scoped>
/* Admin Stats Styles */
.admin-stats-container {
  width: 100%;
  margin-bottom: 20px;
}
/* 

/* Filter Styles */
.makercart-filters {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;
}

.filter-row {
  display: flex;
  gap: 20px;
  align-items: end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-label {
  font-weight: 600;
  color: var(--primary-blue);
  font-size: var(--fs-small);
}

.filter-date-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: var(--fs-small);
  min-width: 150px;
}

.filter-date-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 2px rgba(0, 61, 106, 0.1);
}

.filter-clear-btn {
  padding: 8px 16px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: var(--fs-small);
  transition: background-color 0.2s;
}

.filter-clear-btn:hover:not(:disabled) {
  background-color: #5a6268;
}

.filter-clear-btn:disabled {
  background-color: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

/* Date filter status styles */
.filter-date-input.partial-filter {
  border-color: #ffc107;
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.2);
}

.filter-date-input.ready-filter {
  border-color: #28a745;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
}

.filter-status {
  display: flex;
  align-items: center;
  margin-top: 5px;
}

.filter-hint {
  color: #ffc107;
  font-weight: 500;
}

.filter-applied {
  color: #28a745;
  font-weight: 500;
}

/* Table Styles */
.makercart-loading-state {
  text-align: center;
  padding: 20px;
  font-size: 18px;
  color: #999;
}

.makercart-error-state {
  text-align: center;
  padding: 20px;
  font-size: 18px;
  color: red;
}

.makercart-table-container {
  overflow: hidden;
  min-height: 500px;
}

.makercart-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 20px;
  border-radius: 20px;
}

.makercart-table th,
.makercart-table td {
  padding: 10px;
  border: 0.5px solid #ddd;
  text-align: center;
}

.makercart-table th {
  background-color: var(--primary-blue);
  color: white;
  text-align: left;
  vertical-align: middle;
}

.makercart-table td {
  color: var(--primary-blue);
}

.makercart-table tbody tr:last-child td:first-child {
  border-bottom-left-radius: 10px;
}

.makercart-table tbody tr:last-child td:last-child {
  border-bottom-right-radius: 10px;
}

.dashboard-sortable-header {
  transition: background-color 0.2s;
}

.dashboard-sortable-header:hover {
  background-color: #002a4d !important;
}

.header-with-tooltip {
  display: flex;
  align-items: start;
  justify-content: center;
  gap: 4px;
}

.info-icon {
  color: rgba(255, 255, 255, 0.7);
  cursor: help;
  flex-shrink: 0;
}

.info-icon:hover {
  color: white;
}

/* Spinner Styles */
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: var(--primary-blue);
  animation: spin 1s linear infinite;
  background-color: white;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    width: 100%;
  }

  .filter-date-input {
    min-width: 100%;
  }
}
</style>

<style>
.custom-tooltip-table {
  background-color: #f2f3fb !important;
  color: #3b4648 !important;
  border-radius: 10px !important;
  padding: 1rem !important;
  border: 0.1rem solid #d8dae6 !important;
}

.custom-tooltip-table .v-overlay__content {
  background-color: #f2f3fb !important;
  color: #3b4648 !important;
  border-radius: 10px !important;
  padding: 1rem !important;
  border: 0.1rem solid #d8dae6 !important;
}
</style>
